import { DEFAULT_CURRENCY } from '../../../../../utils/currency';
import {
  UpsellOfferType,
  UpsellTouchpointKey,
  createEmptyStrategy,
  createEmptyTouchpoint,
  createEmptyCheckoutPreEnroll,
} from '../../../../Model/UpsellConfig';

/**
 * Conversions between the stored `upsellConfig` shape (prices in cents, currency
 * lowercase) and the draft the form edits (prices in units, currency uppercase).
 *
 * Kept out of the component so they can be unit-tested — these carry the prices, and
 * isDraftOfferComplete is the only thing standing between a half-filled form and an
 * offer the server rejects.
 */

/** Stored offer → draft offer edited by the form. */
const toDraftOffer = (offer) => {
  if (!offer) {
    return null;
  }
  return {
    type: offer.type || UpsellOfferType.COACHING,
    currency: (offer.currency || DEFAULT_CURRENCY.code).toUpperCase(),
    productId: offer.productId || '',
    productName: offer.productName || '',
    monthlyPrice: offer.monthlyPriceInCents ? offer.monthlyPriceInCents / 100 : '',
    minSubscriptionMonths: offer.minSubscriptionMonths ?? 3,
    price: offer.priceInCents ? offer.priceInCents / 100 : '',
    duration: offer.duration ?? '',
    durationUnit: offer.durationUnit === 'month' ? 'month' : 'week',
    programId: offer.programId || '',
    mealPlanId: offer.mealPlanId || '',
    challengeConfigId: offer.challengeConfigId || '',
  };
};

/** Draft offer → stored shape. Returns null for an untouched offer. */
const fromDraftOffer = (draftOffer) => {
  if (!draftOffer) {
    return null;
  }
  const base = {
    type: draftOffer.type,
    currency: (draftOffer.currency || DEFAULT_CURRENCY.code).toLowerCase(),
    productName: draftOffer.productName || '',
  };
  if (draftOffer.type === UpsellOfferType.CHALLENGE) {
    return {
      ...base,
      productId: draftOffer.productId || '',
      priceInCents: Math.round(Number(draftOffer.price || 0) * 100),
      duration: parseInt(draftOffer.duration, 10) || 0,
      durationUnit: draftOffer.durationUnit === 'month' ? 'month' : 'week',
      programId: draftOffer.programId || '',
      mealPlanId: draftOffer.mealPlanId || '',
      challengeConfigId: draftOffer.challengeConfigId || '',
    };
  }
  return {
    ...base,
    productId: draftOffer.productId || '',
    monthlyPriceInCents: Math.round(Number(draftOffer.monthlyPrice || 0) * 100),
    minSubscriptionMonths: Math.max(1, parseInt(draftOffer.minSubscriptionMonths, 10) || 1),
  };
};

/** Stored touchpoints → draft touchpoints, all three of them. */
const toDraftTouchpoint = (touchpoint) => (touchpoint ? {
  ...createEmptyTouchpoint(),
  enabled: !!touchpoint.enabled,
  message: { ...createEmptyTouchpoint().message, ...touchpoint.message },
  offer: toDraftOffer(touchpoint.offer),
} : createEmptyTouchpoint());

const toDraftTouchpoints = (touchpoints) => Object.fromEntries(Object.values(UpsellTouchpointKey).map(
  (touchpointKey) => [touchpointKey, toDraftTouchpoint(touchpoints?.[touchpointKey])],
));

/**
 * Stored strategy → draft strategy, guaranteeing all three touchpoints and the checkout pre-enroll exist. The pop-up
 * A/B test's group B stays absent until one is stored, so a strategy that never had one saves without it.
 */
const toDraftStrategy = (strategy) => {
  const draft = createEmptyStrategy();
  if (strategy?.checkoutPreEnroll) {
    const { offer } = strategy.checkoutPreEnroll;
    draft.checkoutPreEnroll = {
      ...createEmptyCheckoutPreEnroll(),
      enabled: !!strategy.checkoutPreEnroll.enabled,
      offer: offer && {
        ...toDraftOffer(offer),
        originalMonthlyPrice: offer.originalMonthlyPriceInCents ? offer.originalMonthlyPriceInCents / 100 : '',
      },
    };
  }
  draft.touchpoints = toDraftTouchpoints(strategy?.touchpoints);
  if (strategy?.experiment) {
    draft.experiment = {
      enabled: !!strategy.experiment.enabled,
      touchpoints: toDraftTouchpoints(strategy.experiment.touchpoints),
    };
  }
  return draft;
};

/**
 * The Custom starting point for a challenge: a deep copy of the default strategy minus what
 * cannot be shared. `challengeConfigId` goes because the override must mint its own config
 * rather than write onto the default's; `pendingAttachment` goes because its dataUrl is a blob
 * URL the default's own save revokes, so a copy would fail to upload from either side. Saved
 * media (`message.mediaPath`) is kept.
 */
const snapshotStrategy = (strategy) => {
  const snapshot = JSON.parse(JSON.stringify(strategy));
  // The pop-up A/B test's group B is copied too, under the same rules.
  [snapshot.touchpoints, snapshot.experiment?.touchpoints].filter(Boolean).forEach((touchpoints) => {
    Object.values(UpsellTouchpointKey).forEach((touchpointKey) => {
      const touchpoint = touchpoints[touchpointKey];
      if (touchpoint) {
        delete touchpoint.pendingAttachment;
        if (touchpoint.offer) {
          touchpoint.offer.challengeConfigId = '';
        }
      }
    });
  });
  return snapshot;
};

/**
 * Draft checkout pre-enroll → stored shape, priced in `currency` like every touchpoint's offer, with the original price
 * it is shown discounted from when one is set. A strategy saved before pre-enroll existed has none, and stores it
 * switched off.
 */
const toStoredCheckoutPreEnroll = (checkoutPreEnroll, currency) => {
  const draftOffer = checkoutPreEnroll?.offer;
  const offer = fromDraftOffer(draftOffer && { ...draftOffer, currency });
  const originalMonthlyPriceInCents = Math.round(Number(draftOffer?.originalMonthlyPrice || 0) * 100);
  return {
    enabled: !!checkoutPreEnroll?.enabled,
    offer: offer && originalMonthlyPriceInCents > 0 ? { ...offer, originalMonthlyPriceInCents } : offer,
  };
};

const hasDraftMessage = (touchpoint) => (
  !!touchpoint?.message?.text?.trim() || !!touchpoint?.message?.mediaPath || !!touchpoint?.pendingAttachment
);

const isDraftOfferComplete = (draftOffer) => {
  if (!draftOffer) {
    return false;
  }
  if (draftOffer.type === UpsellOfferType.CHALLENGE) {
    return !!draftOffer.productId
      && Number(draftOffer.price) > 0
      && (parseInt(draftOffer.duration, 10) || 0) >= 1
      && !!draftOffer.programId;
  }
  return !!draftOffer.productId
    && Number(draftOffer.monthlyPrice) > 0
    && (parseInt(draftOffer.minSubscriptionMonths, 10) || 0) >= 1;
};

/**
 * Switched off, or offering a complete 1:1 (the server only pre-enrolls into coaching) whose original price, when set,
 * is above the price charged: anything else would show no discount.
 */
const isDraftCheckoutPreEnrollValid = (checkoutPreEnroll) => {
  if (!checkoutPreEnroll?.enabled) {
    return true;
  }
  const { offer } = checkoutPreEnroll;
  const isOriginalPriceValid = !offer?.originalMonthlyPrice
    || Number(offer.originalMonthlyPrice) > Number(offer.monthlyPrice);
  return offer?.type === UpsellOfferType.COACHING && isDraftOfferComplete(offer) && isOriginalPriceValid;
};

/**
 * The first touchpoint the strategy cannot save as drafted, or undefined. An enabled control touchpoint needs a message
 * or media and a complete offer. While the pop-up A/B test is on, an enabled group B touchpoint needs a complete offer;
 * its message is optional, as it falls back to the control touchpoint's — so one with none needs a control message to
 * fall back to, or group B would get a pop-up with no message. Group B is skipped when `isTestGroupShown` is false: the
 * admin has no box to fix it in, and its stored group is saved as is.
 *
 * @returns {{ touchpointKey: string, isTestGroup: boolean }|undefined}
 */
const findInvalidDraftTouchpoint = (strategy, { isTestGroupShown = true } = {}) => {
  const touchpointKeys = Object.values(UpsellTouchpointKey);
  const invalidControlKey = touchpointKeys.find((touchpointKey) => {
    const touchpoint = strategy.touchpoints[touchpointKey];
    return touchpoint.enabled && (!hasDraftMessage(touchpoint) || !isDraftOfferComplete(touchpoint.offer));
  });
  if (invalidControlKey) {
    return { touchpointKey: invalidControlKey, isTestGroup: false };
  }
  if (!isTestGroupShown || !strategy.experiment?.enabled) {
    return undefined;
  }
  const invalidTestKey = touchpointKeys.find((touchpointKey) => {
    const touchpoint = strategy.experiment.touchpoints[touchpointKey];
    const control = strategy.touchpoints[touchpointKey];
    // A disabled group A never uploads its picked media, so only its stored message can be borrowed.
    const hasMessage = hasDraftMessage(touchpoint)
      || hasDraftMessage(control.enabled ? control : { message: control.message });
    return touchpoint.enabled && (!hasMessage || !isDraftOfferComplete(touchpoint.offer));
  });
  return invalidTestKey ? { touchpointKey: invalidTestKey, isTestGroup: true } : undefined;
};

/*
  The two eligibility rules, kept together: ProductSelector hides `isHidden` products, so a
  product it will not offer must not make the coach eligible either.
*/
const isCoachingProduct = (product) => !product.isHidden && !product.isChallengeProduct;
const isChallengeOfferProduct = (product) => !product.isHidden && !!product.isChallengeProduct;

/**
 * The currency a challenge's upsell offers are priced in. The server bills the
 * client on their own contract and rejects anything else, so this is derived
 * from that contract rather than chosen — the same rule the contract
 * generator's own upsell price field follows. The fallback covers the default
 * strategy, which has no single contract behind it.
 */
const offerCurrencyForContract = (contract, fallbackCurrency) => (
  contract?.currency?.toUpperCase() || fallbackCurrency || DEFAULT_CURRENCY.code
);

export {
  toDraftOffer,
  fromDraftOffer,
  toDraftStrategy,
  snapshotStrategy,
  isDraftOfferComplete,
  findInvalidDraftTouchpoint,
  toStoredCheckoutPreEnroll,
  isDraftCheckoutPreEnrollValid,
  isCoachingProduct,
  isChallengeOfferProduct,
  offerCurrencyForContract,
};
